import React, {useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import PageLayout from '../../components/layout/PageLayout'
import Text from '../../components/text/Text'
import WizButton from '../../components/button/WizButton'
import UnderLineInput from '../../components/input/UnderLineInput'
import WizSpinner from '../../components/spinner/WizSpinner'
import ProbeSummary from './ProbeSummary'
import SuppressedWrites from './SuppressedWrites'
import PhaseTable from './PhaseTable'
import CallList from './CallList'
import ConfirmOrdersModal from './ConfirmOrdersModal'
import {IProbeReport} from './types'
import {copyProbeText} from './utils'
import {getKeyProbe} from '../../apis/user/getKeyProbe'

interface IProps {
    className?: string
}

export function KeyProbe({className}: IProps) {
    const [searchParams] = useSearchParams()
    // ?id=로 들어와도 자동 실행하지 않는다. probe는 거래소로 라이브 호출을 내보내므로 항상 명시적 실행만 받는다.
    const [keyId, setKeyId] = useState(searchParams.get('id') || '')
    const [withOrders, setWithOrders] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [report, setReport] = useState<IProbeReport | null>(null)
    // 실행마다 결과 영역을 새로 마운트해, 접힘 상태가 이전 실행에서 이월되지 않게 한다.
    const [runId, setRunId] = useState(0)

    const run = async (ordersIncluded: boolean) => {
        const id = keyId.trim()
        if (!id) return

        setConfirmOpen(false)
        setLoading(true)
        setError('')

        const res = await getKeyProbe(id, ordersIncluded)

        setLoading(false)
        if (!res?.data) {
            // 5004: id 누락, 5000: 키 없음 또는 이 프로세스에 prober 미구성
            setError(res?.error || res?.Message || `요청이 실패했습니다 (code ${res?.code ?? res?.status})`)
            setReport(null)
            return
        }
        setReport(res.data as IProbeReport)
        setRunId(prev => prev + 1)
    }

    const onRunClick = () => {
        if (!keyId.trim() || loading) return
        // 저장을 일으키는 실행만 확인을 받는다.
        if (withOrders) {
            setConfirmOpen(true)
            return
        }
        run(false)
    }

    return (
        <PageLayout title={'Key Probe'} className={className}>
            <Text className={'text-body2 text-font03'}>
                키 하나로 온디맨드 사이클을 한 번 돌리고 거래소 응답 바디를 원본 그대로 받아옵니다. Key Test가 요약
                판정이라면 이쪽은 "어느 엔드포인트가 무슨 코드로 거절했는지"를 봅니다.
            </Text>

            <div className={'mt-[20px] bg-white rounded-[10px] p-[30px] shadow-normal'}>
                <div className={'flex items-end gap-x-[20px] md:flex-col md:items-stretch md:gap-y-[20px]'}>
                    <div className={'flex-1'}>
                        <Text className={'text-ti3 text-font03'}>Key ID</Text>
                        <UnderLineInput
                            className={'mt-[6px]'}
                            value={keyId}
                            placeholder={'API 키 ID를 입력하세요'}
                            onChanged={e => setKeyId(e || '')}
                            onEnterKeyPress={onRunClick}
                        />
                    </div>

                    <label className={'flex items-center gap-x-[8px] cursor-pointer whitespace-nowrap pb-[3px]'}>
                        <input
                            type={'checkbox'}
                            checked={withOrders}
                            onChange={e => setWithOrders(e.target.checked)}
                            className={'w-[16px] h-[16px] accent-[#0AAF82] cursor-pointer'}
                        />
                        <Text className={'text-body2 text-font01'}>주문 조회 포함 (with_orders)</Text>
                    </label>

                    <WizButton
                        type={withOrders ? 'red' : 'primary'}
                        text={loading ? '실행 중…' : 'Probe 실행'}
                        disabled={loading || !keyId.trim()}
                        onClick={onRunClick}
                    />
                </div>

                {withOrders && (
                    <Text className={'text-body3 text-font_red mt-[16px]'}>
                        주문 조회는 읽기 전용이 아닙니다 — blofin을 뺀 거래소에서는 가져온 주문이 저장됩니다.
                    </Text>
                )}
                <Text className={'text-body3 text-font03 mt-[6px]'}>
                    account·balance phase는 읽기 전용이며, probe는 키 비활성화·알림 발송 같은 sync 경로의 쓰기를 실행
                    대신 기록만 합니다.
                </Text>
            </div>

            {loading && (
                <div className={'mt-[20px] flex items-center gap-x-[12px]'}>
                    <WizSpinner />
                    <Text className={'text-body2 text-font02'}>
                        거래소로 라이브 호출 중입니다. 응답이 수십 KB일 수 있어 몇 초 걸릴 수 있습니다.
                    </Text>
                </div>
            )}

            {error && (
                <div
                    className={
                        'mt-[20px] rounded-[8px] border border-font_red bg-bg_red px-[16px] py-[12px] space-y-[4px]'
                    }>
                    <Text className={'text-ti2 text-font_red'}>{error}</Text>
                    <Text className={'text-body3 text-font02'}>
                        키 ID가 맞는지, 그리고 거래소 레지스트리와 prober가 로드된 서버를 보고 있는지 확인하세요.
                    </Text>
                </div>
            )}

            {report && !loading && (
                <div key={runId} className={'mt-[20px] space-y-[20px]'}>
                    <div className={'flex justify-end'}>
                        <Text
                            onClick={() => copyProbeText(JSON.stringify(report, null, 2), '리포트 전체가')}
                            className={'text-body3 text-sub_blue cursor-pointer'}>
                            리포트 전체 JSON 복사
                        </Text>
                    </div>

                    <ProbeSummary report={report} />
                    <SuppressedWrites items={report.suppressed_writes} />
                    <PhaseTable phases={report.phases} />
                    <CallList calls={report.calls} />
                </div>
            )}

            <ConfirmOrdersModal
                open={confirmOpen}
                keyId={keyId.trim()}
                onConfirm={() => run(true)}
                onCancel={() => setConfirmOpen(false)}
            />
        </PageLayout>
    )
}
